import { cn } from "@/lib/utils";
import Image from "next/image";
import { ChevronLeft, ChevronRight, MoveLeft, MoveRight } from "lucide-react";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import React from "react";
import {SingleProductEnum} from "@/constants/commerce-enum";
import NoImage from "@/public/no-image.jpg";


interface IProps {
    setIsOpen: (isOpen: boolean) => void;
    hasPreviousPrice: boolean;
    hasFreeDelivery: boolean;
    isModal?: boolean;
    selectedImage: string;
    productName: string;
    currentIndex: number;
    handleNext: () => void;
    mediaData: any;
    handlePrevious: () => void;
    handleImageClick: (image: string, index: number) => void;
}

const ImageGallery: React.FC<IProps> = ({
                                            isModal = false,
                                            setIsOpen,
                                            hasPreviousPrice,
                                            hasFreeDelivery,
                                            selectedImage,
                                            currentIndex,
                                            productName,
                                            mediaData,
                                            handleNext,
                                            handlePrevious,
                                            handleImageClick,
                                        }) => (
    <div
        className={cn(
            "flex flex-col",
            isModal
                ? "w-full max-w-2xl flex flex-wrap items-center justify-center mx-auto"
                : "md:min-w-[25px] max-w-[360px] relative"
        )}
    >

        <div
            className={cn(
                "bg-white rounded-2xl overflow-hidden cursor-pointer flex items-center justify-center relative",
                isModal ? "aspect-square  md:w-[500px] h-[370px]" : ""
            )}
            onClick={() => !isModal && setIsOpen(true)}
        >
            {/*Badges*/}
            {!isModal ? (
                <div className={"absolute top-4 right-0 flex flex-col gap-2 "}>
                    {hasPreviousPrice ? (
                        <span
                            className={
                                "w-[75px] h-7 bg-blue-secondary rounded-l text-xs text-white flex items-center justify-center"
                            }
                        >
             {SingleProductEnum.previousPrice}
            </span>
                    ) : null}
                    {hasFreeDelivery ? (
                        <span
                            className={
                                "w-[75px] h-7 bg-blue-secondary rounded-l text-xs text-white flex items-center justify-center"
                            }
                        >
              {SingleProductEnum.freeDelivery}
            </span>
                    ) : null}
                </div>
            ) : null}

            {/*End here*/}
            <Image
                src={selectedImage}
                alt={`${productName} image`}
                title={`${productName} image`}
                width={isModal ? 600 : 370}
                height={isModal ? 600 : 370}
                className="h-full object-contain p-2 border rounded-2xl w-[370px] border-[#294580] border-opacity-50"
            />
            {isModal ? (
                <>
                    <button
                        onClick={handlePrevious}
                        className={cn(
                            "absolute left-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-none border",
                            currentIndex === 0 && "opacity-50 cursor-not-allowed"
                        )}
                        aria-label="Previous image"
                        disabled={currentIndex === 0}
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                        onClick={handleNext}
                        className={cn(
                            "absolute right-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-none border",
                            currentIndex === mediaData?.length - 1 &&
                            "opacity-50 cursor-not-allowed"
                        )}
                        aria-label="Next image"
                        disabled={currentIndex === mediaData?.length - 1}
                    >
                        <ChevronRight className="w-6 h-6" />
                    </button>
                </>
            ) : null}
        </div>
        <div className="flex justify-center mt-4 relative">
            {!isModal ? (
                <button
                    onClick={handlePrevious}
                    className={cn(
                        "absolute left-0 top-1/2 z-10 -translate-y-1/2 bg-white rounded-full p-1 shadow-none",
                        currentIndex === 0 && "hidden"
                    )}
                    aria-label="Previous thumbnail"
                    disabled={currentIndex === 0}
                >
                    <MoveLeft className="w-5 h-5" />
                </button>
            ) : null}
            <ScrollArea className="flex space-x-2 overflow-hidden p-4 w-full items-center justify-center">
                {mediaData?.map((item : any, index : number) => (
                    <button
                        key={index}
                        onClick={() => handleImageClick(item?.url, index)}
                        className={cn(
                            "flex-shrink-0 border rounded-lg overflow-hidden",
                            currentIndex === index ? "border-gray-300" : "border-gray-100",
                            isModal ? "w-16 h-16" : "w-12 h-12"
                        )}
                        aria-label={`View ${productName} image ${index + 1}`}
                    >
                        <Image
                            src={item?.url ?? NoImage}
                            alt={`${productName} thumbnail ${index + 1}`}
                            title={`${productName} thumbnail ${index + 1}`}
                            width={64}
                            height={64}
                            className="w-full h-full object-contain p-1"
                        />
                    </button>
                ))}
            </ScrollArea>
            {!isModal && (
                <button
                    onClick={handleNext}
                    className={cn(
                        "absolute right-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-1",
                        currentIndex === mediaData?.length - 1 && "hidden"
                    )}
                    aria-label="Next thumbnail"
                    disabled={currentIndex === mediaData?.length - 1}
                >
                    <MoveRight className="w-5 h-5" />
                </button>
            )}
        </div>
    </div>
);

export default ImageGallery;